import { supabase } from "@/lib/supabaseClient";
import { fetchCurrentProfile } from "@/services/auth";

export type Kpi = { total: number; cur: number; prev: number };

export type AffiliateDashboard = {
  code: string;
  firstName: string | null;
  points: number;
  level: { title: string; nextTitle: string | null; nextMin: number | null; percent: number };
  kpis: { visits: Kpi; tests: Kpi; prospects: Kpi; hot: Kpi; clients: Kpi };
  week: { days: { day: string; visits: number }[]; invitesSent: number; newSignups: number; newClients: number };
  invitesGoal: { done: number; goal: number };
  achievements: { title: string; description: string; points: number; icon: string; unlockedAt: string }[];
  prizes: { id: string; title: string; pointsCost: number; icon: string }[];
};

export async function fetchAffiliateDashboard(weekOffset = 0): Promise<AffiliateDashboard | null> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const { data, error } = await supabase.rpc("affiliate_dashboard", { p_tz: tz, p_week_offset: weekOffset });
  if (error) throw error;
  return (data as AffiliateDashboard | null) ?? null;
}

export async function fetchLinkCreatedAt(): Promise<Date | null> {
  const profile = await fetchCurrentProfile();
  if (!profile) return null;
  const { data } = await supabase.from("profiles").select("created_at").eq("id", profile.id).maybeSingle();
  return data?.created_at ? new Date(data.created_at as string) : null;
}

export function referralLink(code: string): string {
  return `${window.location.origin}/registro/${code}`;
}

export type ShareChannel = "copy" | "whatsapp" | "share" | "qr";

/** Registra que la persona compartió su link (alimenta "Invitaciones enviadas" y los logros). */
export async function logShare(channel: ShareChannel): Promise<void> {
  const profile = await fetchCurrentProfile();
  if (!profile) return;
  await supabase.from("affiliate_share_events").insert({ organization_id: profile.organizationId, channel });
}

/** % de cambio vs. la semana anterior; null si no hay base para comparar. */
export function deltaPercent(k: Kpi): number | null {
  if (k.prev <= 0) return k.cur > 0 ? 100 : null;
  return Math.round(((k.cur - k.prev) / k.prev) * 100);
}

// ---------------------------------------------------------------------------
// Página pública /registro/:code
// ---------------------------------------------------------------------------

function visitorId(): string {
  try {
    const existing = localStorage.getItem("vv-visitor-id");
    if (existing && existing.length >= 8) return existing;
    const fresh = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`).slice(0, 64);
    localStorage.setItem("vv-visitor-id", fresh);
    return fresh;
  } catch {
    return `anon-${Math.random().toString(36).slice(2, 12)}`;
  }
}

/** Devuelve el nombre de quien invitó (o null si el link no existe) y cuenta la visita. */
export async function openReferralLink(code: string): Promise<string | null> {
  const { data: name } = await supabase.rpc("referral_sponsor_name", { p_code: code });
  if (!name) return null;
  await supabase.rpc("track_referral_visit", { p_code: code, p_visitor: visitorId() });
  return name as string;
}

// ---------------------------------------------------------------------------
// Premios (administración)
// ---------------------------------------------------------------------------

export type Prize = { id: string; title: string; pointsCost: number; icon: string; active: boolean };

export async function fetchPrizes(): Promise<Prize[]> {
  const { data, error } = await supabase
    .from("affiliate_prizes")
    .select("id, title, points_cost, icon, active")
    .order("points_cost", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((p) => ({
    id: p.id as string,
    title: p.title as string,
    pointsCost: p.points_cost as number,
    icon: p.icon as string,
    active: p.active as boolean,
  }));
}

export async function savePrize(input: { id?: string; title: string; pointsCost: number; icon: string }): Promise<void> {
  if (input.id) {
    const { error } = await supabase
      .from("affiliate_prizes")
      .update({ title: input.title, points_cost: input.pointsCost, icon: input.icon })
      .eq("id", input.id);
    if (error) throw error;
    return;
  }
  const profile = await fetchCurrentProfile();
  if (!profile) throw new Error("Debes iniciar sesión.");
  const { error } = await supabase.from("affiliate_prizes").insert({
    organization_id: profile.organizationId,
    title: input.title,
    points_cost: input.pointsCost,
    icon: input.icon,
  });
  if (error) throw error;
}

export async function setPrizeActive(id: string, active: boolean): Promise<void> {
  const { error } = await supabase.from("affiliate_prizes").update({ active }).eq("id", id);
  if (error) throw error;
}

export async function deletePrize(id: string): Promise<void> {
  const { error } = await supabase.from("affiliate_prizes").delete().eq("id", id);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Catálogos visibles para el afiliado
// ---------------------------------------------------------------------------

export type AffiliateLevel = { id: string; title: string; minPoints: number };

export async function fetchLevels(): Promise<AffiliateLevel[]> {
  const { data, error } = await supabase
    .from("affiliate_levels")
    .select("id, title, min_points")
    .order("min_points", { ascending: true });
  if (error) throw error;
  return (data ?? []).map((l) => ({ id: l.id as string, title: l.title as string, minPoints: l.min_points as number }));
}

export type AchievementItem = {
  id: string;
  title: string;
  description: string;
  points: number;
  icon: string;
  unlocked: boolean;
};

export async function fetchAchievementCatalog(): Promise<AchievementItem[]> {
  const { data: userData } = await supabase.auth.getUser();
  const me = userData.user?.id ?? "";
  const [catalog, mine] = await Promise.all([
    supabase.from("affiliate_achievements").select("id, title, description, points, icon").eq("active", true).order("points", { ascending: true }),
    supabase.from("affiliate_user_achievements").select("achievement_id").eq("user_id", me),
  ]);
  if (catalog.error) throw catalog.error;
  const unlocked = new Set((mine.data ?? []).map((r) => r.achievement_id as string));
  return (catalog.data ?? []).map((a) => ({
    id: a.id as string,
    title: a.title as string,
    description: a.description as string,
    points: a.points as number,
    icon: a.icon as string,
    unlocked: unlocked.has(a.id as string),
  }));
}

export async function fetchActivePrizes(): Promise<Prize[]> {
  return (await fetchPrizes()).filter((p) => p.active);
}

// ---------------------------------------------------------------------------
// Mis Prospectos (personas que se registraron con tu link)
// ---------------------------------------------------------------------------

export type ProspectState = "nuevo" | "seguimiento" | "caliente" | "inactivo";
export type InterestLevel = "bajo" | "medio" | "alto" | "muy_alto";

export type ReferredProspect = {
  id: string;
  fullName: string;
  email: string;
  avatarUrl: string | null;
  archetypeKey: string | null;
  createdAt: Date;
  quizAt: Date | null;
  courseAt: Date | null;
  lastActivity: Date;
  lastActivityKind: "registro" | "test" | "curso";
  maxPercent: number;
  state: ProspectState;
  stars: number; // 1..5
  interest: InterestLevel;
  hotSince: Date | null;
};

const DAY = 24 * 60 * 60 * 1000;

function d(v: unknown): Date | null {
  return typeof v === "string" ? new Date(v) : null;
}

export async function fetchReferredProspects(): Promise<ReferredProspect[]> {
  const { data, error } = await supabase.rpc("affiliate_prospects");
  if (error) throw error;
  const now = Date.now();
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => {
    const createdAt = new Date(r.created_at as string);
    const quizAt = d(r.quiz_at);
    const courseAt = d(r.course_at);
    const courseLast = d(r.course_last);
    const maxPercent = Number(r.max_percent ?? 0);

    const candidates: { at: Date; kind: "registro" | "test" | "curso" }[] = [{ at: createdAt, kind: "registro" }];
    if (quizAt) candidates.push({ at: quizAt, kind: "test" });
    if (courseLast) candidates.push({ at: courseLast, kind: "curso" });
    const last = candidates.reduce((a, b) => (b.at > a.at ? b : a));

    const hot = Boolean(quizAt && courseAt);
    const state: ProspectState = hot
      ? "caliente"
      : quizAt
        ? "seguimiento"
        : now - last.at.getTime() > 14 * DAY
          ? "inactivo"
          : "nuevo";

    const score =
      1 +
      (quizAt ? 1 : 0) +
      (courseAt ? 1 : 0) +
      (maxPercent >= 30 ? 1 : 0) +
      (now - last.at.getTime() <= 3 * DAY ? 1 : 0);
    const stars = Math.min(5, score);
    const interest: InterestLevel = stars <= 2 ? "bajo" : stars === 3 ? "medio" : stars === 4 ? "alto" : "muy_alto";

    return {
      id: r.id as string,
      fullName: (r.full_name as string | null) || "Sin nombre",
      email: (r.email as string | null) ?? "",
      avatarUrl: (r.avatar_url as string | null) ?? null,
      archetypeKey: (r.archetype_key as string | null) ?? null,
      createdAt,
      quizAt,
      courseAt,
      lastActivity: last.at,
      lastActivityKind: last.kind,
      maxPercent,
      state,
      stars,
      interest,
      hotSince: hot && quizAt && courseAt ? (quizAt > courseAt ? quizAt : courseAt) : null,
    };
  });
}

export type PersonalityInfo = { key: string; label: string; temperament: string; description: string };

export async function fetchPersonalityProfiles(): Promise<PersonalityInfo[]> {
  const { data, error } = await supabase.from("personality_profiles").select("key, label, temperament, description");
  if (error) throw error;
  return (data ?? []) as PersonalityInfo[];
}

/** Mensaje sugerido (plantilla segura, sin promesas de ingresos) según el estado de la persona. */
export function suggestedMessage(p: Pick<ReferredProspect, "fullName" | "state">): string {
  const n = p.fullName.split(" ")[0];
  switch (p.state) {
    case "caliente":
      return `Hola ${n} 👋 Vi que te interesó el contenido de Vívelo AI. ¿Te gustaría agendar una llamada corta para contarte más?`;
    case "seguimiento":
      return `Hola ${n} 👋 Ya completaste tu test. ¿Quieres conocer tu ruta personalizada para avanzar hacia tus objetivos?`;
    case "inactivo":
      return `Hola ${n} 👋 ¿Todo bien? Estoy aquí para resolver cualquier duda que tengas sobre Vívelo AI.`;
    default:
      return `Hola ${n} 👋 ¡Bienvenido a Vívelo AI! Estoy aquí para ayudarte en lo que necesites.`;
  }
}
